import React, { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { cn } from '../lib/utils';
import {
  LayoutDashboard, Bot, Zap, AlertTriangle, Activity,
  BarChart3, PieChart, FileText, Settings, LogOut, Menu, X,
  ChevronRight, Building2, Search, Shield, GitBranch, Users, Crown, Radio, Power, Mail, Send, Store, Share2, Plug, Wrench, Factory, Rocket
} from 'lucide-react';
import { Button } from './ui/button';
import { NotificationBell } from './NotificationBell';
import { AutonomousStatusBanner } from './AutonomousStatusBanner';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const navSections = [
  {
    label: 'Organization',
    items: [
      { to: '/studio', label: 'Agent Studio', icon: Bot },
      { to: '/studio/org', label: 'Org Chart', icon: GitBranch },
      { to: '/studio/org/teams', label: 'Teams', icon: Users },
      { to: '/studio/marketplace', label: 'Marketplace', icon: Store },
      { to: '/studio/factory', label: 'Agent Factory', icon: Factory },
      { to: '/studio/autonomous', label: 'Autonomous Control', icon: Power },
    ],
  },
  {
    label: 'Build',
    items: [
      { to: '/integrations', label: 'Integration Hub', icon: Plug },
      { to: '/tools', label: 'Tool Studio', icon: Wrench },
      { to: '/gateway', label: 'Comms Gateway', icon: Radio, live: true },
      { to: '/sales-orders', label: 'Sales Orders', icon: FileText },
      { to: '/workflow', label: 'Agent Workflow', icon: GitBranch },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/comms', label: 'Live Communications', icon: Radio, live: true },
      { to: '/comms/inbox', label: 'Message Inbox', icon: Mail },
      { to: '/comms/network', label: 'Network Map', icon: Share2 },
      { to: '/console', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/console/escalations', label: 'Escalations', icon: AlertTriangle, badge: true },
      { to: '/console/activity', label: 'Activity Log', icon: Activity },
      { to: '/console/orchestration', label: 'Orchestration', icon: GitBranch },
      { to: '/console/metrics', label: 'Metrics', icon: BarChart3 },
      { to: '/console/audit', label: 'Audit Trail', icon: Shield },
    ],
  },
  {
    label: 'Executive',
    items: [
      { to: '/cfo', label: 'Command Center', icon: Crown, gold: true },
      { to: '/cfo/briefings', label: 'Briefings', icon: FileText },
      { to: '/cfo/directives', label: 'Directives', icon: Send },
      { to: '/simulation', label: 'Perfect Storm', icon: Zap, live: true },
      { to: '/activate', label: 'Activation Hub', icon: Rocket },
    ],
  },
  {
    label: 'Reports & Portal',
    items: [
      { to: '/portal', label: 'Executive Dashboard', icon: PieChart },
      { to: '/portal/reports', label: 'Reports', icon: FileText },
    ],
  },
];

function NavContent({ onClose }) {
  const { user, tenant, logout } = useAuth();
  const location = useLocation();

  return (
    <div className="flex flex-col h-full" data-testid="app-sidebar">
      {/* Logo */}
      <div className="px-5 py-5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center">
          <Zap className="w-4 h-4 text-white" />
        </div>
        <span className="font-display text-lg font-semibold text-white tracking-tight">Cygnus</span>
      </div>

      {/* Tenant */}
      <div className="px-5 pb-4">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5">
          <Building2 className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs text-slate-300 truncate">Cygnus Corp</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 overflow-y-auto scrollbar-thin">
        {navSections.map((section) => (
          <div key={section.label} className="mb-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 px-3 mb-2 font-medium"
                 data-testid={`sidebar-nav-${section.label.toLowerCase()}`}>
              {section.label}
            </div>
            {section.items.map((item) => {
              const isActive = location.pathname === item.to || 
                (item.to !== '/' && location.pathname.startsWith(item.to) && item.to.split('/').length >= location.pathname.split('/').length);
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onClose}
                  className={cn(
                    'relative group flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors mb-0.5',
                    isActive
                      ? 'bg-white/[0.07] text-white font-medium'
                      : 'text-slate-300/90 hover:bg-white/[0.04] hover:text-white'
                  )}
                >
                  {isActive && (
                    <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-teal-500" />
                  )}
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.label}</span>
                  {item.live && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
                  {item.gold && <span className="text-[9px] text-[#C9A84C] font-medium ml-auto">NEW</span>}
                  {item.badge && (
                    <Badge variant="destructive" className="ml-auto text-[10px] h-4 px-1.5 bg-red-500/90">
                      5
                    </Badge>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-3 pb-4 mt-auto border-t border-white/10 pt-4">
        <NavLink
          to="/settings"
          onClick={onClose}
          className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-300/90 hover:bg-white/[0.04] hover:text-white"
        >
          <Settings className="w-4 h-4" />
          <span>Settings</span>
        </NavLink>
        <div className="flex items-center gap-3 px-3 py-2 mt-2">
          <div className="w-7 h-7 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-medium">
            {user?.name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white truncate font-medium">{user?.name}</p>
            <p className="text-[10px] text-slate-400 truncate">{user?.email}</p>
          </div>
          <button onClick={logout} className="text-slate-400 hover:text-white transition-colors">
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Determine current page title
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.startsWith('/studio/deploy')) return 'Deploy Agent';
    if (path.startsWith('/studio/agents')) return 'Agent Detail';
    if (path === '/studio/org/teams') return 'Team Manager';
    if (path === '/studio/org') return 'Organization Chart';
    if (path === '/studio/autonomous') return 'Autonomous Control';
    if (path === '/studio/marketplace') return 'Agent Marketplace';
    if (path === '/studio/factory') return 'Agent Factory';
    if (path === '/studio') return 'Agent Studio';
    if (path === '/integrations') return 'Integration Hub';
    if (path === '/tools') return 'Tool Studio';
    if (path === '/gateway') return 'Communication Gateway';
    if (path === '/comms/inbox') return 'Message Inbox';
    if (path === '/comms/network') return 'Network Map';
    if (path === '/comms') return 'A2A Communications';
    if (path === '/cfo/briefings') return 'CFO Briefings';
    if (path === '/cfo/directives') return 'CFO Directives';
    if (path === '/simulation') return 'Perfect Storm Simulation';
    if (path === '/activate') return 'Customer Activation';
    if (path.startsWith('/cfo')) return 'CFO Command Center';
    if (path === '/console/escalations') return 'Escalations';
    if (path === '/console/activity') return 'Activity Log';
    if (path === '/console/metrics') return 'Performance Metrics';
    if (path === '/console/audit') return 'Audit Trail';
    if (path === '/console/orchestration') return 'Orchestration';
    if (path === '/console') return 'Operations Console';
    if (path.startsWith('/portal/reports/')) return 'Report Viewer';
    if (path === '/portal/reports') return 'Report Library';
    if (path === '/portal') return 'Executive Dashboard';
    if (path === '/settings') return 'Settings';
    return 'Cygnus';
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-[280px] xl:w-[300px] flex-col bg-[var(--cy-sidebar)] cy-noise shrink-0">
        <NavContent />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[280px] p-0 bg-[var(--cy-sidebar)] border-none">
          <NavContent onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-14 shrink-0 border-b border-slate-200 bg-white flex items-center px-4 lg:px-6 gap-4">
          <button
            className="lg:hidden text-slate-600 hover:text-slate-900"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="font-display text-lg font-semibold text-slate-900">{getPageTitle()}</h1>
          <div className="flex-1" />
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-400 text-sm cursor-pointer hover:bg-slate-200 transition-colors" data-testid="global-command-search">
            <Search className="w-3.5 h-3.5" />
            <span>Search...</span>
            <kbd className="ml-4 text-[10px] bg-slate-200 rounded px-1.5 py-0.5">Ctrl+K</kbd>
          </div>
          <NotificationBell />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1440px] px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
